import {
  CaretLeftIcon,
  FolderSimpleDashedIcon,
  ListChecksIcon,
  MegaphoneIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react';
import type { Page } from '@regia/shared';
import type { CSSProperties } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router';
import { PlatformIcon } from '../../lib/platforms';
import {
  ButtonLink,
  EmptyState,
  folderStyle,
  folderTransitionName,
  ScrollTabs,
  Skeleton,
} from '../../ui';
import { PageBody } from '../common';
import { type SchedaTab, schedaTabs } from '../registry';
import { usePage } from './api';

function HeaderAction({
  to,
  icon: IconCmp,
  label,
}: {
  to: string;
  icon: typeof UploadSimpleIcon;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex h-10 items-center gap-2 rounded-full bg-[color-mix(in_srgb,var(--folder-fg)_16%,transparent)] px-3.5 text-sm font-semibold text-[var(--folder-fg)] transition-colors hover:bg-[color-mix(in_srgb,var(--folder-fg)_24%,transparent)]"
    >
      <IconCmp size={18} weight="bold" aria-hidden />
      {label}
    </Link>
  );
}

function SchedaHeader({ page }: { page: Page }) {
  const platforms = [...new Set(page.channels.map((c) => c.platform))];
  return (
    <PageBody className="pt-2 lg:pt-8">
      <Link
        to="/schede"
        className="-ml-2 mb-2 inline-flex h-10 items-center gap-1 rounded-full px-2 text-[0.9375rem] font-medium text-ink-2 hover:text-ink"
      >
        <CaretLeftIcon size={18} aria-hidden />
        Schede
      </Link>
      <header style={{ viewTransitionName: folderTransitionName(page.id) } as CSSProperties}>
        <div className="flex px-4">
          <h1 className="folder-tab folder-tab-lg">{page.name}</h1>
        </div>
        <div className="folder-body !min-h-0 !pb-4">
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <p className="max-w-prose text-[0.9375rem] leading-relaxed opacity-90">
                {page.description ||
                  (page.kind === 'mio' ? 'La tua pagina.' : 'Pagina di un cliente.')}
              </p>
              {platforms.length > 0 ? (
                <div className="mt-3 flex items-center gap-2 opacity-90">
                  {platforms.map((p) => (
                    <PlatformIcon key={p} platform={p} size={20} />
                  ))}
                </div>
              ) : null}
            </div>
            {page.logoUrl ? (
              <img
                src={page.logoUrl}
                alt=""
                className="h-14 w-14 shrink-0 rounded-full bg-white object-cover ring-2 ring-white/50"
              />
            ) : null}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <HeaderAction
              to={`/carica?scheda=${page.slug}`}
              icon={UploadSimpleIcon}
              label="Carica"
            />
            <HeaderAction
              to={`/agenda/nuovo-post?scheda=${page.slug}`}
              icon={MegaphoneIcon}
              label="Nuovo post"
            />
            <HeaderAction
              to={`/attivita?nuova=1&scheda=${page.slug}`}
              icon={ListChecksIcon}
              label="Attività"
            />
          </div>
        </div>
      </header>
    </PageBody>
  );
}

export function SchedaPage() {
  const { slug } = useParams();
  const query = usePage(slug);

  if (query.isLoading) {
    return (
      <PageBody className="pt-6">
        <Skeleton className="h-12 w-1/2 rounded-t-[14px]" />
        <Skeleton className="h-36 w-full rounded-[18px]" />
      </PageBody>
    );
  }
  if (!query.data) {
    return (
      <PageBody className="pt-8">
        <EmptyState
          icon={FolderSimpleDashedIcon}
          title="Scheda non trovata"
          message="Forse è stata eliminata o il link è sbagliato."
          action={<ButtonLink to="/schede">Vai alle schede</ButtonLink>}
        />
      </PageBody>
    );
  }
  const page = query.data;
  const tabs = schedaTabs.map((t) => ({
    id: t.id || 'panoramica',
    label: t.label,
    to: t.id ? `/schede/${page.slug}/${t.id}` : `/schede/${page.slug}`,
    end: !t.id,
  }));

  return (
    <div style={folderStyle(page.color)} className="pb-6">
      <SchedaHeader page={page} />
      <ScrollTabs
        items={tabs}
        className="sticky top-0 z-20 mt-3 bg-paper/95 backdrop-blur-md lg:px-10"
      />
      <PageBody className="pt-5">
        <Outlet context={page} />
      </PageBody>
    </div>
  );
}

/** Rende il componente di una tab passando la scheda corrente. */
export function SchedaTabRoute({ tab }: { tab: SchedaTab }) {
  const page = useOutletContext<Page>();
  const Element = tab.element;
  return <Element page={page} />;
}
